
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Input, Badge } from '../components/UI';
import { apiRequest } from '../utils/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { useAuth } from '../components/AuthContext';
import { UserRole, NewsItem } from '../types';
import { 
    Save, ArrowLeft, Bold, Italic, Underline, List, ListOrdered, 
    Code, Type, AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, 
    Image as ImageIcon, Undo, Redo, Eye, Edit3, Calendar, User, Clock, Upload
} from 'lucide-react';
import { API_BASE_URL } from '../config';

export const NewsEditor: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); // If ID exists, we are editing

    const [formData, setFormData] = useState({
        title: '',
        category: 'ANNOUNCEMENT',
        content: '',
        published_at: ''
    });
    
    const [editorMode, setEditorMode] = useState<'VISUAL' | 'HTML' | 'PREVIEW'>('VISUAL');
    const [loading, setLoading] = useState(false);
    const contentEditableRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load existing data if editing
    useEffect(() => {
        if (id) {
            setLoading(true);
            // Fetch specific ID to bypass list filters (allows editing future posts)
            apiRequest<any>(`news.php?id=${id}`).then(res => {
                // Handle both single object or array return
                const item = Array.isArray(res) ? res[0] : res;
                
                if (item) {
                    // Convert API date to input format (YYYY-MM-DDTHH:mm)
                    const pubDate = item.published_at ? item.published_at.replace(' ', 'T').substring(0, 16) : '';
                    
                    setFormData({
                        title: item.title,
                        category: item.category as string,
                        content: item.content,
                        published_at: pubDate
                    });
                    if (contentEditableRef.current) {
                        contentEditableRef.current.innerHTML = item.content;
                    }
                }
            }).catch(err => {
                alert("Fehler beim Laden der News: " + err.message);
            }).finally(() => setLoading(false));
        }
    }, [id]);

    // Handle Content Updates
    const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFormData({ ...formData, content: e.target.value });
    };

    const handleVisualInput = (e: React.FormEvent<HTMLDivElement>) => {
        setFormData({ ...formData, content: e.currentTarget.innerHTML });
    };

    // Toolbar Commands
    const execCmd = (command: string, value: string | undefined = undefined) => {
        document.execCommand(command, false, value);
        // Force update state
        if (contentEditableRef.current) {
            setFormData({ ...formData, content: contentEditableRef.current.innerHTML });
        }
    };

    const switchMode = (mode: 'VISUAL' | 'HTML' | 'PREVIEW') => {
        // Sync Visual content to state before switching away
        if (editorMode === 'VISUAL' && contentEditableRef.current) {
            setFormData(prev => ({ ...prev, content: contentEditableRef.current!.innerHTML }));
        }
        setEditorMode(mode);
    };

    // Ensure Div matches state when switching back to Visual
    useEffect(() => {
        if (editorMode === 'VISUAL' && contentEditableRef.current) {
            if (contentEditableRef.current.innerHTML !== formData.content) {
                contentEditableRef.current.innerHTML = formData.content;
            }
        }
    }, [editorMode]);

    // HANDLE IMAGE UPLOAD
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading(true);
        const uploadData = new FormData();
        uploadData.append('image', file);

        try {
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const res = await fetch(`${baseUrl}/upload_news_image.php`, {
                method: 'POST',
                body: uploadData
            });
            const json = await res.json();
            
            if (json.status === 'success') {
                // Construct full URL
                let finalUrl = json.url;
                if (!finalUrl.startsWith('http')) {
                    finalUrl = `${baseUrl}/${finalUrl}`;
                }
                
                // Insert into editor
                if (editorMode === 'VISUAL') {
                    // Restore focus
                    contentEditableRef.current?.focus();
                    execCmd('insertImage', finalUrl);
                } else {
                    // Append HTML manually
                    const imgTag = `<img src="${finalUrl}" alt="News Image" style="max-width:100%; border-radius: 8px; margin: 10px 0;" />`;
                    setFormData(prev => ({...prev, content: prev.content + imgTag}));
                }
            } else {
                alert("Upload Fehler: " + json.message);
            }
        } catch (err: any) {
            alert("Netzwerkfehler: " + err.message);
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        // Sync before save if in visual mode
        if (editorMode === 'VISUAL' && contentEditableRef.current) {
             setFormData(prev => ({ ...prev, content: contentEditableRef.current!.innerHTML }));
        }

        if (!formData.title) {
            alert("Ein Titel ist erforderlich.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                title: formData.title,
                content: formData.content,
                category: formData.category,
                published_at: formData.published_at || null,
                author_id: user?.id
            };

            if (id) {
                await apiRequest('news.php', 'PUT', { ...payload, id: parseInt(id) });
            } else {
                await apiRequest('news.php', 'POST', payload);
            }
            alert(formData.published_at ? "News eingeplant!" : "News veröffentlicht!");
            navigate('/news');
        } catch (e: any) {
            alert("Fehler: " + e.message);
        } finally {
            setLoading(false);
        }
    };

    if (!user?.roles.includes(UserRole.ADMIN) && !user?.roles.includes(UserRole.WEBADMIN)) {
        return <div className="p-10 text-center">Zugriff verweigert.</div>;
    }

    const ToolbarBtn = ({ icon: Icon, cmd, arg, active }: any) => (
        <button 
            onMouseDown={(e) => { e.preventDefault(); execCmd(cmd, arg); }}
            className={`p-2 rounded hover:bg-gray-700 transition-colors ${active ? 'bg-gray-700 text-white' : 'text-gray-400'}`}
            title={cmd}
        >
            <Icon size={16} />
        </button>
    );

    const getBadgeColor = (cat: string) => {
        if (cat === 'DECISION') return 'red';
        if (cat === 'OFFICIAL') return 'blue';
        return 'yellow';
    };

    return (
        <div className="max-w-6xl mx-auto animate-fade-in pb-20">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <button onClick={() => navigate('/news')} className="flex items-center text-gray-500 hover:text-white mb-2 text-xs font-bold uppercase tracking-widest transition-colors">
                        <ArrowLeft size={14} className="mr-1"/> Zurück zum Feed
                    </button>
                    <h1 className="text-3xl font-bold uppercase italic text-white">
                        {id ? 'News bearbeiten' : 'News erstellen'}
                    </h1>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => navigate('/news')}>Abbrechen</Button>
                    <Button onClick={handleSave} icon={Save} disabled={loading}>
                        {loading ? 'Speichere...' : formData.published_at ? 'Einplanen' : 'Veröffentlichen'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* SETTINGS SIDEBAR */}
                <div className="space-y-6 order-2 lg:order-1">
                    <Card title="Einstellungen">
                        <div className="space-y-4">
                            <Input 
                                label="Titel" 
                                value={formData.title} 
                                onChange={e => setFormData({...formData, title: e.target.value})} 
                                placeholder="Schlagzeile..."
                            />
                            
                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase">Kategorie</label>
                                <select 
                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white"
                                    value={formData.category}
                                    onChange={e => setFormData({...formData, category: e.target.value})}
                                >
                                    <option value="ANNOUNCEMENT">Announcement (Allgemein)</option>
                                    <option value="OFFICIAL">Official (Wichtig/Ping)</option>
                                    <option value="DECISION">Decision (Steward)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-gray-400 text-xs font-bold mb-2 uppercase flex items-center gap-2">
                                    <Clock size={12}/> Veröffentlichung (Optional)
                                </label>
                                <input 
                                    type="datetime-local" 
                                    className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-white text-sm"
                                    value={formData.published_at}
                                    onChange={e => setFormData({...formData, published_at: e.target.value})}
                                />
                                <p className="text-[10px] text-gray-500 mt-1">
                                    Leer lassen für sofort. Wenn Datum in Zukunft: Post erscheint erst dann und wird erst dann an Discord gesendet.
                                </p>
                            </div>

                            <div className="text-xs text-gray-500 bg-gray-900/50 p-3 rounded border border-gray-800">
                                <p className="mb-2"><strong>Editor Info:</strong></p>
                                <ul className="list-disc pl-4 space-y-1">
                                    <li><strong>Visual:</strong> Schreibmodus wie in Word.</li>
                                    <li><strong>HTML:</strong> Für Embeds (YouTube/Twitter) oder Feinschliff.</li>
                                    <li><strong>Preview:</strong> Zeigt, wie es im Feed aussieht.</li>
                                </ul>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* EDITOR MAIN AREA */}
                <div className="lg:col-span-2 order-1 lg:order-2">
                    <Card className="min-h-[600px] flex flex-col p-0 overflow-hidden bg-[#1e1e1e]">
                        {/* TOOLBAR */}
                        <div className="p-2 bg-[#25252b] border-b border-gray-700 flex flex-wrap gap-1 items-center sticky top-0 z-10">
                            <div className="flex gap-1 border-r border-gray-700 pr-2 mr-1">
                                <button 
                                    onClick={() => switchMode('VISUAL')}
                                    className={`px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2 transition-colors ${editorMode === 'VISUAL' ? 'bg-f1-red text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}
                                >
                                    <Edit3 size={14}/> Visual
                                </button>
                                <button 
                                    onClick={() => switchMode('HTML')}
                                    className={`px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2 transition-colors ${editorMode === 'HTML' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}
                                >
                                    <Code size={14}/> HTML
                                </button>
                                <button 
                                    onClick={() => switchMode('PREVIEW')}
                                    className={`px-3 py-1.5 rounded text-xs font-bold flex items-center gap-2 transition-colors ${editorMode === 'PREVIEW' ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}
                                >
                                    <Eye size={14}/> Preview
                                </button>
                            </div>

                            {editorMode === 'VISUAL' && (
                                <>
                                    <ToolbarBtn icon={Undo} cmd="undo" />
                                    <ToolbarBtn icon={Redo} cmd="redo" />
                                    <div className="w-px h-6 bg-gray-700 mx-1"></div>
                                    <ToolbarBtn icon={Bold} cmd="bold" />
                                    <ToolbarBtn icon={Italic} cmd="italic" />
                                    <ToolbarBtn icon={Underline} cmd="underline" />
                                    <div className="w-px h-6 bg-gray-700 mx-1"></div>
                                    <ToolbarBtn icon={ListOrdered} cmd="insertOrderedList" />
                                    <ToolbarBtn icon={List} cmd="insertUnorderedList" />
                                    <div className="w-px h-6 bg-gray-700 mx-1"></div>
                                    <ToolbarBtn icon={AlignLeft} cmd="justifyLeft" />
                                    <ToolbarBtn icon={AlignCenter} cmd="justifyCenter" />
                                    <ToolbarBtn icon={AlignRight} cmd="justifyRight" />
                                    <div className="w-px h-6 bg-gray-700 mx-1"></div>
                                    
                                    {/* LINK */}
                                    <button 
                                        className="p-2 rounded hover:bg-gray-700 text-gray-400"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            const url = prompt('Link URL:');
                                            if (url) execCmd('createLink', url);
                                        }}
                                        title="Link einfügen"
                                    >
                                        <LinkIcon size={16}/>
                                    </button>

                                    {/* URL IMAGE */}
                                    <button 
                                        className="p-2 rounded hover:bg-gray-700 text-gray-400"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            const url = prompt('Bild URL (Direktlink):');
                                            if (url) execCmd('insertImage', url);
                                        }}
                                        title="Bild per URL"
                                    >
                                        <ImageIcon size={16}/>
                                    </button>

                                    {/* UPLOAD IMAGE */}
                                    <button 
                                        className="p-2 rounded hover:bg-gray-700 text-blue-400"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            fileInputRef.current?.click();
                                        }}
                                        title="Bild hochladen"
                                    >
                                        <Upload size={16}/>
                                    </button>
                                    <input 
                                        type="file" 
                                        ref={fileInputRef} 
                                        className="hidden" 
                                        accept="image/*" 
                                        onChange={handleImageUpload} 
                                    />
                                </>
                            )}
                            {editorMode === 'PREVIEW' && <div className="text-xs text-gray-400 italic ml-2">Vorschau-Modus</div>}
                        </div>

                        {/* EDITING SURFACE */}
                        <div className="flex-1 relative bg-[#1e1e1e]">
                            {editorMode === 'VISUAL' && (
                                <div
                                    ref={contentEditableRef}
                                    contentEditable
                                    className="w-full h-full min-h-[600px] p-6 text-gray-200 outline-none prose prose-invert max-w-none overflow-y-auto"
                                    onInput={handleVisualInput}
                                    style={{ whiteSpace: 'pre-wrap' }}
                                />
                            )}
                            
                            {editorMode === 'HTML' && (
                                <textarea
                                    className="w-full h-full min-h-[600px] p-4 bg-[#151515] text-green-400 font-mono text-sm resize-none focus:outline-none"
                                    value={formData.content}
                                    onChange={handleContentChange}
                                    spellCheck={false}
                                />
                            )}

                            {editorMode === 'PREVIEW' && (
                                <div className="w-full h-full min-h-[600px] p-8 bg-black/40 overflow-y-auto flex justify-center">
                                    <div className="w-full max-w-3xl">
                                        <div className="bg-f1-carbon border border-gray-600 rounded-lg flex flex-col shadow-2xl relative">
                                            {/* Preview Header */}
                                            <div className="p-6 border-b border-gray-700 flex justify-between items-start">
                                                <div>
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <Badge color={getBadgeColor(formData.category) as any}>
                                                            {formData.category}
                                                        </Badge>
                                                        <span className="text-gray-500 text-xs flex items-center gap-1">
                                                            <Calendar size={12}/> {formData.published_at ? new Date(formData.published_at).toLocaleDateString() : new Date().toLocaleDateString()}
                                                        </span>
                                                        <span className="text-gray-500 text-xs flex items-center gap-1">
                                                            <User size={12}/> {user?.username} (Vorschau)
                                                        </span>
                                                    </div>
                                                    <h2 className="text-2xl font-bold text-white">{formData.title || "Kein Titel"}</h2>
                                                </div>
                                            </div>
                                            
                                            {/* Preview Content */}
                                            <div className="p-6 overflow-y-auto text-gray-200 leading-relaxed custom-html-content">
                                <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(formData.content || '<p class="text-gray-500 italic">Kein Inhalt...</p>') }} />
                                            </div>

                                            <div className="p-4 border-t border-gray-700 bg-gray-900/50 rounded-b-lg flex justify-between items-center text-xs text-gray-500">
                                                <span>Preview Mode</span>
                                                <span>Sunday Driver Cup</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};
